import React, { useCallback, useEffect, useRef, useState } from "react";
import LudDashboardModal from "../../modals/LudDashboardModal";
import { ConfirmModal } from "../../modals/Confirm";
import { Button, Checkbox, Input, NoticeBox, StatusDropdown, Tags } from "../../ui";
import {
  cancelChangeRequestAPI,
  completeSubscriptionAPI,
  getApplicationCredentialsAPI,
  getApplicationDetailAPI,
  integrationDecisionAPI,
  subscriptionActionAPI,
} from "../../../utils/api/applications";
import { errorMessage } from "./applicationsText";
import { BRANCH_TAG, CHANGE_STATUS_TAG, REQUEST_TAG, REQUEST_TYPE_TAG, STATE_TAG } from "./applicationsModel";
import ReviewRequestModal from "./ReviewRequestModal";

const formatDate = (value, lang) => (value ? new Date(value).toLocaleString(lang === "ar" ? "ar-SA" : "en-GB", { dateStyle: "medium", timeStyle: "short" }) : "—");

// Everything about one application of the selected store: its subscription
// requests (send / retry / cancel for owners, complete for admins), its
// integration (account, manager, per-branch ids) and the administrators'
// decisions. Every action is repeated and enforced by the backend.
export default function ManageModal({ open, storeId, app, appName, lang, t, token, canManage, canReview, reviewId = null, onClose, onChanged, onIntegrate, onAddBranches, onActivateBranches, onDeactivate, onEditRequest }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [selected, setSelected] = useState([]);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const [credentials, setCredentials] = useState(null);
  const [reviewing, setReviewing] = useState(null);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    try {
      const res = await getApplicationDetailAPI(token, storeId, app.key);
      if (!alive.current) return;
      setDetail(res.data);
      setError("");
      // Keep an open review pointing at the latest version of its request.
      setReviewing((current) => (current ? res.data.changeRequests.find((item) => item.id === current.id && item.status === "inprogress") || null : current));
    } catch (e) {
      if (alive.current) setError(errorMessage(t, e));
    } finally {
      if (alive.current) setLoading(false);
    }
  }, [token, storeId, app?.key, t]);

  useEffect(() => {
    if (!open || !app) return;
    setLoading(true);
    setDetail(null);
    setSelected([]);
    setRejecting(false);
    setReason("");
    setCredentials(null);
    setReviewing(null);
    load();
  }, [open, app, load]);

  const autoReview = useRef(null);
  useEffect(() => {
    if (!reviewId || !detail || autoReview.current === reviewId) return;
    const target = detail.changeRequests.find((item) => item.id === reviewId && item.status === "inprogress");
    autoReview.current = reviewId;
    if (target) setReviewing(target);
  }, [reviewId, detail]);

  if (!open || !app) return null;

  const integration = detail?.integration;
  const requests = detail?.requests || [];
  const changeRequests = detail?.changeRequests || [];
  const state = integration?.state || app.state;
  const hasIntegration = Boolean(integration?.account) || (integration?.branches || []).some((branch) => branch.integration !== "unconfigured");
  const pendingBranches = (integration?.branches || []).filter((branch) => branch.integration === "pending");
  const storeStatus = integration?.storeStatus;

  // Runs a server action, refreshes the details and tells the page.
  const run = async (action, message) => {
    setBusy(true);
    setError("");
    try {
      const result = await action();
      await load();
      onChanged?.(message || t.manage.done, result?.request?.status === "failed" ? "error" : "success");
    } catch (e) {
      if (alive.current) {
        setError(errorMessage(t, e));
        await load();
      }
      onChanged?.(null);
    } finally {
      if (alive.current) setBusy(false);
    }
  };

  const ask = (title, message, action, successMessage) =>
    setConfirm({
      title,
      message,
      onConfirm: async () => {
        await run(action, successMessage);
        setConfirm(null);
      },
    });

  const decide = (body, message) => run(() => integrationDecisionAPI(token, storeId, app.key, body), message);
  const toggleSelected = (id) => setSelected((current) => (current.includes(id) ? current.filter((item) => item !== id) : [...current, id]));

  const revealPassword = async () => {
    if (credentials) {
      setCredentials(null);
      return;
    }
    try {
      const res = await getApplicationCredentialsAPI(token, storeId, app.key);
      setCredentials(res.data.password || "");
    } catch (e) {
      setError(errorMessage(t, e));
    }
  };

  const renderRequest = (request) => {
    const canSend = canManage && ["pending", "failed"].includes(request.status);
    return (
      <li key={request.id} className="lud-app-request">
        <div className="lud-app-request-head">
          <strong dir="ltr">{request.reference}</strong>
          <Tags variant={REQUEST_TAG[request.status] || "neutral"} appearance="soft" size="sm">{t.status[request.status] || request.status}</Tags>
        </div>
        <div className="lud-app-tags">
          {request.branches.map((branch) => (
            <Tags key={branch.branchId} variant="neutral" appearance="soft" size="sm">{branch.name}</Tags>
          ))}
        </div>
        <small>{formatDate(request.sentAt || request.createdAt, lang)}</small>
        {request.status === "failed" && request.lastError && <small className="lud-app-error">{request.lastError}</small>}
        {request.possiblyDelivered && <small className="lud-app-error">{t.manage.possiblyDelivered}</small>}
        <div className="lud-app-actions">
          {canSend && (
            <Button size="sm" variant="primary" appearance="soft" disabled={busy} onClick={() => ask(t.confirm.sendTitle, t.confirm.sendMsg, () => subscriptionActionAPI(token, storeId, request.id, "send"), t.subscribe.sentOk)}>
              {request.status === "failed" ? t.manage.retry : t.manage.send}
            </Button>
          )}
          {canSend && (
            <Button size="sm" variant="danger" appearance="soft" disabled={busy} onClick={() => ask(t.confirm.cancelTitle, t.confirm.cancelMsg, () => subscriptionActionAPI(token, storeId, request.id, "cancel"))}>
              {t.manage.cancelRequest}
            </Button>
          )}
          {canReview && request.status === "sent" && (
            <Button size="sm" variant="success" appearance="filled" disabled={busy} onClick={() => ask(t.confirm.completeTitle, t.confirm.completeMsg, () => completeSubscriptionAPI(token, request.id))}>
              {t.manage.complete}
            </Button>
          )}
        </div>
        <details className="lud-app-history">
          <summary>{t.manage.history}</summary>
          <ul>
            {request.history.map((item, index) => (
              <li key={`${item.at}-${index}`}>
                <span>{formatDate(item.at, lang)}</span> <strong>{item.action}</strong>
                {item.to ? ` → ${t.status[item.to] || item.to}` : ""}
                {item.by ? ` · ${item.by}` : ""}
                {item.note ? ` · ${item.note}` : ""}
              </li>
            ))}
          </ul>
        </details>
      </li>
    );
  };

  return (
    <>
    <LudDashboardModal open title={t.manage.title(appName)} size="lg" onClose={onClose} busy={busy}>
      {loading && <p className="lud-app-lead" role="status">{t.loading}</p>}
      {error && (
        <NoticeBox variant="danger" size="sm">
          {error}{" "}
          {!detail && <Button size="sm" variant="neutral" appearance="soft" onClick={() => { setLoading(true); load(); }}>{t.retry}</Button>}
        </NoticeBox>
      )}

      {detail && (
        <div className="lud-app-stack">
          <div className="lud-app-summary">
            <Tags variant={STATE_TAG[state] || "neutral"} appearance="soft">{t.status[state] || state}</Tags>
            <span>{t.branchesCount(integration.activeBranches, integration.totalBranches)}</span>
          </div>

          {!canManage && !canReview && <small className="lud-app-hint">{t.manage.readOnly}</small>}
          {state === "closed" && !canReview && <NoticeBox variant="info" size="sm">{t.manage.reactivateInfo}</NoticeBox>}
          {state === "integrationRejected" && integration.account?.rejectionReason && (
            <NoticeBox variant="warning" size="sm" title={t.manage.reason}>{integration.account.rejectionReason}</NoticeBox>
          )}

          <section aria-labelledby="app-requests">
            <h4 id="app-requests">{t.manage.requests}</h4>
            {requests.length ? <ul className="lud-app-request-list">{requests.map(renderRequest)}</ul> : <small>{t.manage.noRequests}</small>}
          </section>

          <section aria-labelledby="app-changes">
            <h4 id="app-changes">{t.changes.title}</h4>
            {changeRequests.length ? (
              <ul className="lud-app-request-list">
                {changeRequests.map((request) => (
                  <li key={request.id} className="lud-app-request">
                    <div className="lud-app-request-head">
                      <span className="lud-app-tags">
                        <Tags variant={REQUEST_TYPE_TAG[request.type]} appearance="soft" size="sm">{request.scope === "store" ? `${t.requestTypes[request.type]} · ${t.flow.wholeAppTag}` : t.requestTypes[request.type]}</Tags>
                        <Tags variant={CHANGE_STATUS_TAG[request.status] || "neutral"} appearance="soft" size="sm">{t.changeStatus[request.status] || request.status}</Tags>
                      </span>
                      <small dir="ltr">{request.reference} · {t.changes.version(request.version)}</small>
                    </div>
                    <div className="lud-app-tags">
                      {request.changes.map((change) => (
                        <Tags key={change.branchId} variant="neutral" appearance="soft" size="sm">{`${change.name}: ${t.status[change.from] || change.from} → ${t.status[change.to] || change.to}`}</Tags>
                      ))}
                    </div>
                    <small>{`${t.changes.by}: ${request.requestedBy || "—"} · ${formatDate(request.updatedAt, lang)}`}</small>
                    {request.reason && <small className="lud-app-reason-text">{`${t.flow.reasonLabel}: ${request.reason}`}</small>}
                    {request.status === "rejected" && request.rejectionReason && <small className="lud-app-error">{`${t.changes.rejection}: ${request.rejectionReason}`}</small>}
                    {request.status === "inprogress" && (
                      <div className="lud-app-actions">
                        {canManage && <Button size="sm" variant="primary" appearance="soft" disabled={busy} onClick={() => onEditRequest(app, request)}>{t.changes.edit}</Button>}
                        {canManage && (
                          <Button size="sm" variant="danger" appearance="soft" disabled={busy} onClick={() => ask(t.changes.cancelTitle, t.changes.cancelMsg, () => cancelChangeRequestAPI(token, storeId, request.id))}>
                            {t.changes.cancel}
                          </Button>
                        )}
                        {canReview && <Button size="sm" variant="success" appearance="filled" disabled={busy} onClick={() => setReviewing(request)}>{t.changes.review}</Button>}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <small>{t.changes.none}</small>
            )}
          </section>

          {hasIntegration && (
            <section aria-labelledby="app-integration">
              <h4 id="app-integration">{t.manage.integration}</h4>
              <dl className="lud-app-kv">
                <dt>{t.manage.account}</dt>
                <dd dir="ltr">{integration.account ? `${integration.account.id} · ${integration.account.email}` : "—"}</dd>
                <dt>{t.manage.manager}</dt>
                <dd>{integration.manager ? `${integration.manager.name} · ${integration.manager.phone} · ${integration.manager.email}` : "—"}</dd>
                {canReview && integration.account?.hasPassword && (
                  <>
                    <dt>{t.manage.password}</dt>
                    <dd>
                      <span dir="ltr">{credentials !== null ? credentials : "••••••••"}</span>{" "}
                      <Button size="xs" variant="neutral" appearance="text" onClick={revealPassword}>
                        {credentials !== null ? t.manage.hidePassword : t.manage.showPassword}
                      </Button>
                    </dd>
                  </>
                )}
              </dl>

              <div className="lud-app-table-wrap">
                <table className="lud-app-table">
                  <thead>
                    <tr>
                      {canReview && <th aria-label={t.manage.selectBranches} />}
                      <th>{t.manage.branches}</th>
                      <th>{t.manage.appId}</th>
                      <th>{t.manage.appName}</th>
                      <th>{t.manage.statusCol}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {integration.branches
                      .filter((branch) => branch.integration !== "unconfigured")
                      .map((branch) => (
                        <tr key={branch.id}>
                          {canReview && (
                            <td>
                              <Checkbox checked={selected.includes(branch.id)} onChange={() => toggleSelected(branch.id)} aria-label={branch.name} disabled={busy || branch.integration !== "pending"} />
                            </td>
                          )}
                          <td>{branch.name}</td>
                          <td dir="ltr">{branch.appId}</td>
                          <td>{branch.appName}</td>
                          <td>
                            {canReview && ["active", "inactive"].includes(branch.integration) ? (
                              <StatusDropdown
                                value={branch.integration}
                                options={["active", "inactive"]}
                                getLabel={(value) => t.status[value]}
                                editable={!busy}
                                onChange={(next) =>
                                  next === "inactive"
                                    ? onDeactivate(app, { branchIds: [branch.id] })
                                    : decide({ action: "reactivate", scope: "branches", branchIds: [branch.id] })
                                }
                              />
                            ) : (
                              <Tags variant={BRANCH_TAG[branch.integration] || "neutral"} appearance="soft" size="sm" title={branch.integration === "pending" ? t.manage.pendingBranchHint : undefined}>
                                {t.status[branch.integration] || branch.integration}
                              </Tags>
                            )}
                            {branch.integration === "active" && !branch.effective && <small className="lud-app-reason"> · {t.status.closed}</small>}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>

              {canReview && (
                <div className="lud-app-actions">
                  {pendingBranches.length > 0 && storeStatus !== "closed" && (
                    <Button size="sm" variant="success" appearance="filled" disabled={busy || !selected.length} onClick={() => decide({ action: "approve", scope: "branches", branchIds: selected })} title={!selected.length ? t.manage.selectBranches : undefined}>
                      {t.manage.approve}
                    </Button>
                  )}
                  {storeStatus === "inProgress" && (
                    <Button size="sm" variant="danger" appearance="soft" disabled={busy} onClick={() => setRejecting((value) => !value)}>
                      {t.manage.reject}
                    </Button>
                  )}
                  {storeStatus === "closed" && (
                    <Button size="sm" variant="success" appearance="filled" disabled={busy} onClick={() => decide({ action: "reactivate", scope: "store" })}>
                      {t.manage.reactivateStore}
                    </Button>
                  )}
                </div>
              )}

              {canReview && rejecting && (
                <div className="lud-app-reject">
                  <Input textarea label={t.manage.rejectReason} value={reason} onChange={(event) => setReason(event.target.value)} required />
                  <Button size="sm" variant="danger" appearance="filled" disabled={busy || reason.trim().length < 3} onClick={() => decide({ action: "reject", scope: "store", reason: reason.trim() })}>
                    {t.manage.reject}
                  </Button>
                </div>
              )}
            </section>
          )}

          {canManage && (
            <div className="lud-app-actions">
              <Button variant="primary" appearance="soft" disabled={busy} onClick={() => onIntegrate(app)}>
                {hasIntegration ? t.flow.editInfo : t.manage.integrateNow}
              </Button>
              {integration.branches.some((branch) => branch.integration !== "active") && (
                <Button variant="success" appearance="soft" disabled={busy} onClick={() => onActivateBranches(app)}>{t.flow.activateBtn}</Button>
              )}
              {integration.branches.some((branch) => branch.deactivationEligible) && (
                <Button variant="danger" appearance="soft" disabled={busy} onClick={() => onDeactivate(app)}>{t.flow.deactivateBtn}</Button>
              )}
              {integration.storeStatus === "active" && (
                <Button variant="danger" appearance="soft" disabled={busy} onClick={() => onDeactivate(app, { scope: "store" })}>{t.flow.deactivateStoreBtn}</Button>
              )}
              <Button variant="neutral" appearance="soft" disabled={busy} onClick={() => onAddBranches(app)}>{t.manage.addBranches}</Button>
            </div>
          )}
        </div>
      )}

    </LudDashboardModal>
    <ReviewRequestModal
      open={Boolean(reviewing)}
      request={reviewing}
      t={t}
      token={token}
      onClose={() => setReviewing(null)}
      onDone={(message, type) => {
        setReviewing(null);
        load();
        onChanged?.(message, type);
      }}
      onStale={(message) => {
        load();
        onChanged?.(message, "error");
      }}
    />
    {confirm && (
      <ConfirmModal
        title={confirm.title}
        message={confirm.message}
        confirmText={t.confirm.yes}
        cancelText={t.confirm.no}
        onConfirm={confirm.onConfirm}
        onCancel={() => setConfirm(null)}
      />
    )}
    </>
  );
}
